// Counterblast Effect
// A high-fidelity, ultra-premium animation for the "Counterblast" card.
// Narrative: Reflect all damage back to the attacker.
// Breakdown:
// 1. Shield Formation (0 - 800ms): A spherical energy shield forms around the caster's avatar.
// 2. Incoming Attack (800 - 1500ms): An enemy beam hits the shield.
// 3. Absorption (1500 - 2500ms): Shield turns from protective cyan/blue to overloaded glowing orange/gold.
// 4. Reflection Blast (2500 - 4500ms): Fires a massive golden gradient beam back at the attacker.
// 5. Dissipation (4500 - 5000ms): Fades out.

const CONFIG = {
    duration: 5000,
    shieldForm: 800,
    incomingHitStart: 800,
    incomingHitSustain: 1500,
    shieldOverload: 2500,
    blastFire: 2500,
    blastFireEnd: 4500,
    shieldRadius: 65,
};

class BeamParticle {
    x: number; y: number; vx: number; vy: number; life: number; maxLife: number; size: number; color: string;
    constructor(x: number, y: number, vx: number, vy: number, color: string) {
        this.x = x; this.y = y; this.vx = vx; this.vy = vy;
        this.maxLife = 20 + Math.random() * 30;
        this.life = this.maxLife;
        this.size = 2 + Math.random() * 5;
        this.color = color;
    }
    update(dt: number) {
        this.x += this.vx * dt * 0.05;
        this.y += this.vy * dt * 0.05;
        this.life -= dt * 0.05;
    }
    draw(ctx: CanvasRenderingContext2D, alpha: number) {
        if (this.life <= 0) return;
        ctx.globalAlpha = (this.life / this.maxLife) * alpha;
        ctx.fillStyle = this.color;
        ctx.shadowBlur = 10;
        ctx.shadowColor = this.color;

        const speed = Math.hypot(this.vx, this.vy);
        const angle = Math.atan2(this.vy, this.vx);
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(angle);
        ctx.scale(1 + speed * 0.15, 1);
        ctx.beginPath();
        ctx.arc(0, 0, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
}

export type CounterblastEffect = {
    kind: 'counterblast';
    t: number;
    sx: number; // Defender (Shield center)
    sy: number; // Defender (Shield center)
    tx: number; // Attacker (Beam source, then target)
    ty: number; // Attacker
    data?: {
        particles: BeamParticle[];
        lastT: number;
    }
}

export const createCounterblastEffect = (sx: number, sy: number, tx: number, ty: number): CounterblastEffect => ({
    kind: 'counterblast', t: 0, sx, sy, tx, ty
});

// Helper for rendering the 4-point star flare
function drawFlare(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, rotation: number, alpha: number) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.globalAlpha = alpha;

    // Core white flare
    ctx.fillStyle = '#ffffff';
    ctx.shadowBlur = 30;
    ctx.shadowColor = '#ff8800';

    ctx.beginPath();
    ctx.moveTo(0, -50 * scale);
    ctx.quadraticCurveTo(5 * scale, -5 * scale, 50 * scale, 0);
    ctx.quadraticCurveTo(5 * scale, 5 * scale, 0, 50 * scale);
    ctx.quadraticCurveTo(-5 * scale, 5 * scale, -50 * scale, 0);
    ctx.quadraticCurveTo(-5 * scale, -5 * scale, 0, -50 * scale);
    ctx.fill();

    ctx.restore();
}

export const renderCounterblastEffect = (
    eff: CounterblastEffect,
    ctx: CanvasRenderingContext2D
): boolean => {

    if (eff.t >= CONFIG.duration) return false;

    if (!eff.data) {
        eff.data = {
            particles: [],
            lastT: Date.now()
        };
    }

    const dt = Date.now() - eff.data.lastT;
    eff.data.lastT = Date.now();
    const d = eff.data;

    // Vector from Defender (sx) to Attacker (tx) -> For the reflection blast
    const dxOut = eff.tx - eff.sx;
    const dyOut = eff.ty - eff.sy;
    const dist = Math.hypot(dxOut, dyOut);
    const nxOut = dxOut / dist;
    const nyOut = dyOut / dist;

    // Vector from Attacker (tx) to Defender (sx) -> For incoming attack
    const nxIn = -nxOut;
    const nyIn = -nyOut;

    // Shield impact point (edge of the shield)
    const impactX = eff.sx + nxIn * -CONFIG.shieldRadius; // actually + nxOut * shieldRadius
    const impactY = eff.sy + nyIn * -CONFIG.shieldRadius;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // ============================================
    // 1. DRAW SPHERICAL SHIELD
    // ============================================
    let shieldAlpha = 0;

    if (eff.t < CONFIG.shieldForm) {
        shieldAlpha = eff.t / CONFIG.shieldForm;
    } else if (eff.t < CONFIG.blastFireEnd) {
        shieldAlpha = 1;
    } else {
        shieldAlpha = 1 - (eff.t - CONFIG.blastFireEnd) / (CONFIG.duration - CONFIG.blastFireEnd);
    }

    // Color interpolation for Shield: Cyan -> Golden Orange
    let sr = 0, sg = 200, sb = 255; // Cyan
    if (eff.t > CONFIG.incomingHitSustain) {
        const overloadProgress = Math.min(1, (eff.t - CONFIG.incomingHitSustain) / (CONFIG.shieldOverload - CONFIG.incomingHitSustain));
        sr = Math.floor(0 + overloadProgress * 255);
        sg = Math.floor(200 - overloadProgress * 50); // goes to 150
        sb = Math.floor(255 - overloadProgress * 255); // goes to 0 (Orange: 255, 150, 0)
    }

    if (shieldAlpha > 0) {
        let currentRadius = CONFIG.shieldRadius;

        // Tremble logic during absorption
        let cx = eff.sx;
        let cy = eff.sy;
        if (eff.t >= CONFIG.incomingHitStart && eff.t < CONFIG.shieldOverload) {
            const tremble = (eff.t - CONFIG.incomingHitStart) / (CONFIG.shieldOverload - CONFIG.incomingHitStart);
            cx += (Math.random() - 0.5) * 8 * tremble;
            cy += (Math.random() - 0.5) * 8 * tremble;
            currentRadius += Math.sin(eff.t * 0.1) * 3 * tremble;
        }

        ctx.save();
        ctx.globalAlpha = shieldAlpha;

        // Shield Bubble Radial Gradient
        const grad = ctx.createRadialGradient(cx, cy, currentRadius * 0.5, cx, cy, currentRadius);
        grad.addColorStop(0, `rgba(${sr}, ${sg}, ${sb}, 0)`);
        grad.addColorStop(0.8, `rgba(${sr}, ${sg}, ${sb}, 0.2)`);
        grad.addColorStop(1, `rgba(${sr}, ${sg}, ${sb}, 0.8)`);

        ctx.beginPath();
        ctx.arc(cx, cy, currentRadius, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Shield Edge Glow
        ctx.lineWidth = 3;
        ctx.strokeStyle = `rgba(${sr}, ${sg}, ${sb}, ${shieldAlpha})`;
        ctx.shadowBlur = 20;
        ctx.shadowColor = `rgb(${sr}, ${sg}, ${sb})`;
        ctx.stroke();

        // 3D Grid Lines (Simulating sphere wireframe rotating)
        ctx.beginPath();
        for (let i = 1; i < 4; i++) {
            const radX = currentRadius * (i / 4) * Math.abs(Math.cos(eff.t * 0.002));
            ctx.ellipse(cx, cy, radX, currentRadius, 0, 0, Math.PI * 2);

            const radY = currentRadius * (i / 4) * Math.abs(Math.sin(eff.t * 0.002));
            ctx.ellipse(cx, cy, currentRadius, radY, 0, 0, Math.PI * 2);
        }
        ctx.lineWidth = 1;
        ctx.strokeStyle = `rgba(${sr}, ${sg}, ${sb}, ${shieldAlpha * 0.4})`;
        ctx.shadowBlur = 0;
        ctx.stroke();

        ctx.restore();
    }


    // ============================================
    // 2. INCOMING ENEMY ATTACK (Beam)
    // ============================================
    if (eff.t >= CONFIG.incomingHitStart && eff.t < CONFIG.shieldOverload) {
        const inProgress = Math.min(1, (eff.t - CONFIG.incomingHitStart) / 400); // Takes 400ms to visually connect
        const fadeOut = eff.t > 2200 ? 1 - (eff.t - 2200) / 300 : 1;

        if (fadeOut > 0) {
            // Draw incoming magenta/red beam
            const drawDist = dist * inProgress;
            const beamEndX = eff.tx + nxIn * drawDist;
            const beamEndY = eff.ty + nyIn * drawDist;

            ctx.beginPath();
            ctx.moveTo(eff.tx, eff.ty);
            // Cap it at the shield surface
            if (dist - drawDist < CONFIG.shieldRadius) {
                ctx.lineTo(impactX, impactY);
                // Spawn impact sparks
                if (Math.random() < 0.6) {
                    const sparkSpd = 5 + Math.random() * 8;
                    const angle = Math.atan2(nyOut, nxOut) + (Math.random() - 0.5) * Math.PI; // bounce off shield
                    d.particles.push(new BeamParticle(impactX, impactY, Math.cos(angle) * sparkSpd, Math.sin(angle) * sparkSpd, '#ff00aa'));
                }

                // Impact flash
                ctx.beginPath();
                ctx.arc(impactX, impactY, 20 + Math.random() * 10, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 0, 100, ${fadeOut})`;
                ctx.fill();

            } else {
                ctx.lineTo(beamEndX, beamEndY);
            }

            ctx.lineWidth = 8;
            ctx.strokeStyle = `rgba(255, 50, 150, ${fadeOut * 0.8})`;
            ctx.shadowBlur = 20;
            ctx.shadowColor = '#ff0055';
            ctx.stroke();

            ctx.lineWidth = 3;
            ctx.strokeStyle = `rgba(255, 200, 255, ${fadeOut})`;
            ctx.stroke();
            ctx.shadowBlur = 0;
        }
    }


    // ============================================
    // 3. REFLECTION BLAST (Golden Gradient Beam)
    // ============================================
    if (eff.t >= CONFIG.blastFire && eff.t < CONFIG.duration) {
        let beamLength = 0;
        let beamAlpha = 1;
        let isImpacting = false;

        if (eff.t < CONFIG.blastFireEnd) {
            // Firing aggressively
            const extension = (eff.t - CONFIG.blastFire) / 300; // Fast 300ms travel
            beamLength = Math.min(1, extension) * dist;
            isImpacting = (beamLength >= dist);
        } else {
            // Fading
            beamLength = dist;
            beamAlpha = 1 - (eff.t - CONFIG.blastFireEnd) / (CONFIG.duration - CONFIG.blastFireEnd);
        }

        if (beamAlpha > 0) {
            const endX = eff.sx + nxOut * beamLength;
            const endY = eff.sy + nyOut * beamLength;

            const baseThickness = 45; // Match card artwork thickness

            // Layer 3 (Outer): Orange/Reddish Glow
            ctx.beginPath();
            ctx.moveTo(impactX, impactY); // Fire from edge of shield
            ctx.lineTo(endX, endY);
            ctx.lineWidth = baseThickness;
            ctx.strokeStyle = `rgba(255, 60, 0, ${beamAlpha * 0.5})`;
            ctx.shadowBlur = 35;
            ctx.shadowColor = '#ff4400';
            ctx.stroke();

            // Layer 2 (Mid): Golden Yellow
            ctx.beginPath();
            ctx.moveTo(impactX, impactY);
            ctx.lineTo(endX, endY);
            ctx.lineWidth = baseThickness * 0.5;
            ctx.strokeStyle = `rgba(255, 200, 50, ${beamAlpha * 0.8})`;
            ctx.shadowBlur = 15;
            ctx.shadowColor = '#ffaa00';
            ctx.stroke();

            // Layer 1 (Core): Pure White
            ctx.beginPath();
            ctx.moveTo(impactX, impactY);
            ctx.lineTo(endX, endY);
            ctx.lineWidth = baseThickness * 0.15;
            ctx.strokeStyle = `rgba(255, 255, 255, ${beamAlpha})`;
            ctx.shadowBlur = 0;
            ctx.stroke();

            // Flare at the shield (source)
            drawFlare(ctx, impactX, impactY, 2 + Math.random() * 0.5, eff.t * 0.01, beamAlpha);

            // Impact at the target
            if (isImpacting) {
                // Flare at the opponent
                drawFlare(ctx, eff.tx, eff.ty, 1.5 + Math.random() * 0.5, -eff.t * 0.01, beamAlpha);

                // Target hit sparks
                if (Math.random() < 0.6 * beamAlpha) {
                    for (let i = 0; i < 3; i++) {
                        const angle = Math.atan2(nyOut, nxOut) + (Math.random() - 0.5) * Math.PI;
                        const speed = 15 + Math.random() * 15;
                        // Golden sparks
                        d.particles.push(new BeamParticle(eff.tx, eff.ty, Math.cos(angle) * speed, Math.sin(angle) * speed, '#ffaa00'));
                    }
                }
            }
        }
    }

    // Process all particles
    for (let i = d.particles.length - 1; i >= 0; i--) {
        d.particles[i].update(dt);
        d.particles[i].draw(ctx, 1);
        if (d.particles[i].life <= 0) {
            d.particles.splice(i, 1);
        }
    }

    ctx.restore();
    return true;
};
